import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { user, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  // Billing page is always accessible (even without completed onboarding)
  const isBillingPage = location.pathname === '/dashboard/billing';

  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user?.id) {
        setIsChecking(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_settings')
          .select('onboarding_completed')
          .eq('user_id', user.id)
          .maybeSingle();

        // If no record exists or there's an error, try to create one
        if (error || !data) {
          console.log('[OnboardingGuard] No user_settings found, creating...');
          
          await supabase
            .from('user_settings')
            .upsert({
              user_id: user.id,
              onboarding_completed: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            }, { onConflict: 'user_id' });

          setNeedsOnboarding(true);
          setIsChecking(false);
          return;
        }

        // Check the onboarding_completed flag
        const isCompleted = data.onboarding_completed as boolean;
        console.log('[OnboardingGuard] Status:', isCompleted ? 'completed' : 'pending');
        setNeedsOnboarding(!isCompleted);
        setIsChecking(false);
      } catch (err) {
        console.error('[OnboardingGuard] Unexpected error:', err);
        // On error, redirect to onboarding for safety
        setNeedsOnboarding(true);
        setIsChecking(false);
      }
    };

    if (!authLoading) {
      checkOnboarding();
    }
  }, [user?.id, authLoading]);

  if (authLoading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect to onboarding if needed (except billing page)
  if (needsOnboarding && !isBillingPage) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
